import React, { Fragment, lazy, useCallback, useMemo, useRef } from 'react';

import { di } from 'react-magnetic-di';
import { graphql, loadQuery, useFragment, usePreloadedQuery } from 'react-relay';

import RelayEnvironment from '@atlassian/atlassian-studio-api/relay-environment';
import { getMappedEnv } from '@atlassian/atlassian-studio-env-utils';
import type { initAnalyticsClient } from '@atlassian/atlassian-studio-observability';
import {
	extractWorkspaceOrSiteIdFromPath,
	isStudioV2,
	setCachedWorkspaceId,
	setIsStudioV2,
	type URLContext,
} from '@atlassian/atlassian-studio-routing';
import { PageMainContentLoadingLogo } from '@atlassian/atlassian-studio-ui-components';
import {
	featureGatesOptions,
	useInitialiseFeatureGatesClient,
} from '@atlassian/studio-foundations-feature-gates';

import type { AppSelector_userDataFragment$key } from './__generated__/AppSelector_userDataFragment.graphql';
import AppSelectorQuery, {
	type AppSelectorQuery as AppSelectorQueryType,
} from './__generated__/AppSelectorQuery.graphql';
import { isAtlassianEmployee } from './common/utils/account-utils';
import { StudioHeartbeat } from './controllers/heartbeat';
import { useWorkspaceContext } from './hooks/use-workspace-context';
import { sendTrackedRootWorkspaceResolution } from './hooks/use-workspace-context/use-root-workspace-resolution-tracker';

const LazyDevToolsDialogAndToggle = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_studio-foundations-feature-gates" */ '@atlassian/studio-foundations-feature-gates'
	).then((module) => ({
		default: module.DevToolsDialogAndToggle,
	})),
);

const LazyLegacyAtlassianStudioApp = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_atlassian-studio" */ '@atlassian/atlassian-studio'
	).then((module) => ({
		default: module.AtlassianStudioApp,
	})),
);

const LazyStudioFoundationsApp = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_studio-foundations" */ './StudioFoundationsApp'
	).then((module) => ({
		default: module.StudioFoundationsApp,
	})),
);

const routerQueryReference = loadQuery<AppSelectorQueryType>(
	RelayEnvironment,
	AppSelectorQuery,
	{},
);

const isProd = getMappedEnv() === 'production';

export const AppSelector = (): React.JSX.Element => {
	di(sendTrackedRootWorkspaceResolution);
	const data = usePreloadedQuery(
		graphql`
			query AppSelectorQuery {
				me {
					user @required(action: THROW) {
						accountId @required(action: THROW)
						...AppSelector_userDataFragment
						...StudioFoundationsApp_AtlassianStudioFoundations_userDataFragment
					}
				}
				notifications {
					...StudioFoundationsApp_AtlassianStudioFoundations_notificationDataFragment
				}
			}
		`,
		routerQueryReference,
	);

	const userData = useFragment<AppSelector_userDataFragment$key>(
		graphql`
			fragment AppSelector_userDataFragment on User {
				... on AtlassianAccountUser {
					email
				}
			}
		`,
		data.me.user,
	);

	const userAccountId = data.me.user.accountId;
	const trackedLegacyAccounts = useRef(new Set<string>());
	const onAnalyticsClientReady = useCallback(
		(
			analyticsClient: Pick<
				ReturnType<typeof initAnalyticsClient>,
				'setUserInfo' | 'sendOperationalEvent'
			>,
		) => {
			if (trackedLegacyAccounts.current.has(userAccountId)) {
				return;
			}
			trackedLegacyAccounts.current.add(userAccountId);
			void sendTrackedRootWorkspaceResolution({
				analyticsClient,
				atlassianAccountId: userAccountId,
			});
		},
		[userAccountId],
	);
	const userEmail = userData.email ?? undefined;

	const isInternalUser = isAtlassianEmployee(userEmail);

	const { initialized: fgPartlyInitialized, failReason: fgInitFailReason } =
		useInitialiseFeatureGatesClient(
			{
				atlassianAccountId: userAccountId,
			},
			featureGatesOptions,
			{
				isAtlassianEmployee: isInternalUser,
			},
		);

	// If initial feature-gate bootstrap fails, still resolve workspace context so workspace routes can
	// continue into the permanent v2 experience instead of falling back to the legacy app.
	const shouldResolveWorkspaceContext = fgPartlyInitialized || Boolean(fgInitFailReason);
	const urlContext = useMemo<URLContext>(
		() =>
			shouldResolveWorkspaceContext
				? extractWorkspaceOrSiteIdFromPath()
				: { workspaceId: undefined, siteId: undefined },
		[shouldResolveWorkspaceContext],
	);
	// Gather workspace context
	const workspaceContext = useWorkspaceContext(
		urlContext.workspaceId,
		urlContext.siteId,
		userAccountId,
		shouldResolveWorkspaceContext,
	);

	// @NOTE: if the feature gates are not yet initialized, and there is no
	// failure reason, then show the loading spinner
	// if they do fail, for some reason, just proceed so we dont block the user
	if (!fgPartlyInitialized && !fgInitFailReason) {
		return (
			<PageMainContentLoadingLogo
				testId="studio-foundations.app-selector.loading-logo"
				interactionName="studio-foundations-app-selector"
			/>
		);
	}
	// show loading while gathering workspace context
	if (!workspaceContext.isReady) {
		return (
			<PageMainContentLoadingLogo
				testId="studio-foundations.app-selector.loading-logo"
				interactionName="studio-foundations-app-selector"
			/>
		);
	}

	// set v2 flag based on workspace context. Note that redirect would've already happened if it was necessary.
	// if there's an error, workspaceContext.workspaces will be empty, so we'll fallback to v1
	const isV2Value = workspaceContext.workspaces.length > 0;
	const workspaceIdToCache = urlContext.workspaceId ?? workspaceContext.workspaces[0];
	if (workspaceIdToCache) {
		setCachedWorkspaceId(workspaceIdToCache);
	}
	setIsStudioV2(isV2Value);
	// @NOTE: For now, all errors fallback to v1. When v1 is deprecated in the future,
	// we'll add error page handling here for critical errors.

	return (
		<Fragment>
			{isStudioV2() ? (
				<LazyStudioFoundationsApp
					userDataFragment={data.me.user}
					notificationDataFragment={data.notifications ?? undefined}
				/>
			) : (
				<LazyLegacyAtlassianStudioApp onAnalyticsClientReady={onAnalyticsClientReady} />
			)}
			<StudioHeartbeat />
			{/* @NOTE: deploy the ui dev tools modal just once for both v1 and v2 applications */}
			{!isProd || isInternalUser ? <LazyDevToolsDialogAndToggle /> : null}
		</Fragment>
	);
};
